import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { usePageMetadata } from '../../app/PageMetaContext.jsx';
import { useApi, useMutation } from '../../lib/useApi.js';
import { api } from '../../lib/apiClient.js';
import {
  EmptyState,
  ErrorState,
  LoadingSkeleton,
  PageHeader,
  StatusChip,
} from '../../components/primitives.jsx';
import {
  KpiHeroBand,
  GaugeCard,
  MissionImpactHeatmap,
  StaggerReveal,
  toast,
} from '../../components/index.js';
import { buildEnhancementReportPath } from '../briefings/reportEntryLinks.js';
import './admin.css';
import './compliance.redesign.css';

const ENHANCEMENT_REPORT_ENTRY_PATH = buildEnhancementReportPath();

// Map a domain posture status to a 0–100 readiness score and an
// executive-traffic-light tone. Color is derived only from real posture data.
const STATUS_SCORE = { ready: 100, warning: 60, degraded: 25, error: 10 };
const STATUS_TONE = { ready: 'sage', warning: 'amber', degraded: 'brick', error: 'brick' };

function statusScore(status) {
  return STATUS_SCORE[String(status || '').toLowerCase()] ?? 60;
}

function statusTone(status) {
  return STATUS_TONE[String(status || '').toLowerCase()] ?? 'amber';
}

// Coverage-percentage tone using the same traffic-light bands the page already
// applies to records-metadata coverage (>=80 good, >=50 warn, else degraded).
function coverageTone(pct) {
  if (pct >= 80) return 'sage';
  if (pct >= 50) return 'amber';
  return 'brick';
}

function ReadinessItem({ item }) {
  if (!item) return null;
  return (
    <li>
      <div className="row spread gap-sm" style={{ alignItems: 'flex-start' }}>
        <strong>{item.label}</strong>
        <StatusChip status={item.status || 'ready'}>
          {String(item.status || 'ready').toUpperCase()}
        </StatusChip>
      </div>
      <small>{item.detail}</small>
    </li>
  );
}

function PolicyMeta({ policy }) {
  return (
    <div className="compliance-policy-meta">
      <span className="chip outline sm">{policy.classification || 'Sensitive'}</span>
      <span className="chip outline sm">{policy.displayPolicy}</span>
      {policy.aiContextEnabled ? (
        <span className="chip blue sm">AI context allowed</span>
      ) : (
        <span className="chip amber sm">AI excluded</span>
      )}
      {policy.publicStatusEnabled ? (
        <span className="chip blue sm">Public status eligible</span>
      ) : (
        <span className="chip amber sm">Public status blocked</span>
      )}
      {policy.exportEnabled ? (
        <span className="chip outline sm">Export allowed</span>
      ) : (
        <span className="chip warn sm">Export restricted</span>
      )}
    </div>
  );
}

function HistoryList({ items = [], emptyTitle, emptyDescription, renderLabel, renderMeta }) {
  if (!items.length) {
    return <EmptyState title={emptyTitle} description={emptyDescription} />;
  }

  return (
    <ul className="compliance-history-list">
      {items.map((item) => (
        <li key={item.id}>
          <Link className="compliance-history-link" to={item.route || '#'}>
            {renderLabel(item)}
          </Link>
          <small>{renderMeta(item)}</small>
        </li>
      ))}
    </ul>
  );
}

export function ComplianceReadinessPage() {
  const readinessRes = useApi('/readiness');
  const summaryRes = useApi('/compliance/summary');
  const supportIssuesRes = useApi('/operations/support-issues', { query: { limit: 20 } });
  const convertMutation = useMutation();
  const navigate = useNavigate();
  // SM-07: convert-to-action gives success/failure feedback with a link to the
  // created action, per-row busy state, and refreshes the queue afterwards.
  const [convertingIssueId, setConvertingIssueId] = useState(null);

  usePageMetadata({
    title: 'Compliance Readiness',
    subtitle: 'Security posture, operations visibility, and support escalation for DHA PRIME.',
  });

  async function convertToAction(issue) {
    setConvertingIssueId(issue.id);
    try {
      const response = await convertMutation.run(() =>
        api.post(`/operations/support-issues/${issue.id}/convert-to-action`, {}),
      );
      const payload = response?.data || response;
      const actionId = payload?.action?.id;
      toast.success({
        title: 'Support issue converted to action',
        body: `${issue.id} → ${actionId || 'new action'} now tracked in the Action Center.`,
        action: { label: 'Open Action Center', onClick: () => navigate('/actions') },
      });
      await supportIssuesRes.refresh();
    } catch (error) {
      toast.error({ title: 'Convert to action failed', body: error.message });
    } finally {
      setConvertingIssueId(null);
    }
  }

  if (readinessRes.loading || summaryRes.loading || supportIssuesRes.loading) {
    return (
      <>
        <PageHeader title="Compliance Readiness" />
        <LoadingSkeleton rows={6} />
      </>
    );
  }
  if (readinessRes.error) return <ErrorState error={readinessRes.error} />;
  if (summaryRes.error) return <ErrorState error={summaryRes.error} />;
  if (supportIssuesRes.error) return <ErrorState error={supportIssuesRes.error} />;

  const readiness = readinessRes.data?.data;
  const summary = summaryRes.data?.data;
  const supportIssues = supportIssuesRes.data?.data || [];
  const readinessSubsystems = readiness?.subsystems || {};
  const readinessItems = [
    readinessSubsystems.api,
    readinessSubsystems.database,
    readinessSubsystems.seedConfig,
    readinessSubsystems.aiProvider,
    readinessSubsystems.ingestionConnectors,
    readinessSubsystems.exportReport,
  ].filter(Boolean);
  const controlDomains = [
    {
      label: 'CUI / PHI / PII',
      status: (summary?.fieldPolicySummary?.sensitiveFieldCount || 0) > 0 ? 'ready' : 'warning',
      detail: `${summary?.fieldPolicySummary?.sensitiveFieldCount || 0} sensitive fields governed by explicit disclosure policy.`,
    },
    {
      label: 'RBAC',
      status: (summary?.overview?.openAccessReviewCount || 0) > 0 ? 'warning' : 'ready',
      detail: `${summary?.overview?.openAccessReviewCount || 0} access reviews currently require action.`,
    },
    {
      label: 'Audit',
      status: (summary?.operations?.auditEventCount || 0) > 0 ? 'ready' : 'warning',
      detail: `${summary?.operations?.auditEventCount || 0} recent audit events are available for reviewer traceability.`,
    },
    {
      label: 'Exports',
      status: (summary?.operations?.exportFailureCount || 0) > 0 ? 'warning' : 'ready',
      detail: `${summary?.operations?.exportFailureCount || 0} failed or blocked exports require attention.`,
    },
    {
      label: 'AI',
      status:
        (summary?.overview?.pendingAiReviewCount || 0) > 0 ||
        (summary?.operations?.guardrailBlockCount || 0) > 0
          ? 'warning'
          : 'ready',
      detail: `${summary?.overview?.pendingAiReviewCount || 0} pending reviews and ${summary?.operations?.guardrailBlockCount || 0} guardrail blocks remain open.`,
    },
    {
      label: 'Ingestion',
      status:
        (summary?.dataOperations?.failedImportCount || 0) > 0 ||
        (summary?.dataOperations?.failedSyncCount || 0) > 0
          ? 'warning'
          : 'ready',
      detail: `${summary?.dataOperations?.failedImportCount || 0} failed imports and ${summary?.dataOperations?.failedSyncCount || 0} failed syncs are currently tracked.`,
    },
    {
      label: 'Deployment',
      status: readiness?.status === 'ready' ? 'ready' : readiness?.status || 'warning',
      detail: `Environment posture is ${String(readiness?.status || 'warning')} with export and database checks visible in the readiness dashboard.`,
    },
    {
      label: 'Records metadata',
      status:
        (summary?.overview?.recordsMetadataCoveragePercent || 0) >= 80
          ? 'ready'
          : (summary?.overview?.recordsMetadataCoveragePercent || 0) >= 50
            ? 'warning'
            : 'degraded',
      detail: `${summary?.overview?.recordsMetadataCoveragePercent || 0}% of governed artifacts carry records metadata.`,
    },
  ];

  // --- Redesign derived models (reuse already-fetched data only) ---------

  // Overall readiness signal = share of control domains currently "ready".
  const readyDomainCount = controlDomains.filter((d) => d.status === 'ready').length;
  const overallScore = controlDomains.length
    ? Math.round((readyDomainCount / controlDomains.length) * 100)
    : 0;
  const overallTone = readiness?.status || (overallScore >= 80 ? 'ready' : 'warning');

  // Coverage gauges (% by domain) — surface coverage signals the page already
  // shows only as flat numbers, rendered as half-radial gauges.
  const classificationPct = summary?.overview?.classificationCoveragePercent || 0;
  const recordsMetadataPct = summary?.overview?.recordsMetadataCoveragePercent || 0;
  const subsystemValues = Object.values(readinessSubsystems || {});
  const readySubsystemCount = subsystemValues.filter((s) => s?.status === 'ready').length;
  const subsystemReadinessPct = subsystemValues.length
    ? Math.round((readySubsystemCount / subsystemValues.length) * 100)
    : 0;
  const domainReadinessPct = overallScore;

  const coverageGauges = [
    {
      id: 'classification',
      value: classificationPct,
      label: 'Classification coverage',
      sublabel: 'Artifacts with explicit sensitivity tags',
      tone: coverageTone(classificationPct),
    },
    {
      id: 'records-metadata',
      value: recordsMetadataPct,
      label: 'Records metadata',
      sublabel: 'Artifacts with records / disposition metadata',
      tone: coverageTone(recordsMetadataPct),
    },
    {
      id: 'subsystem-readiness',
      value: subsystemReadinessPct,
      label: 'Subsystem readiness',
      sublabel: `${readySubsystemCount}/${subsystemValues.length || 0} subsystem checks green`,
      tone: coverageTone(subsystemReadinessPct),
    },
    {
      id: 'domain-readiness',
      value: domainReadinessPct,
      label: 'Control-domain readiness',
      sublabel: `${readyDomainCount}/${controlDomains.length} domains green`,
      tone: coverageTone(domainReadinessPct),
    },
  ];

  // Control-domain heatmap: rows = domains, single readiness column, cells carry
  // the numeric readiness score + traffic-light tone derived from real posture.
  const heatmapColumns = [{ key: 'readiness', label: 'Readiness' }];
  const heatmapRows = controlDomains.map((d) => ({
    portfolioId: d.label,
    label: d.label,
  }));
  const heatmapCells = controlDomains.map((d) => ({
    portfolioId: d.label,
    dimensionKey: 'readiness',
    portfolioLabel: d.label,
    dimensionLabel: 'Readiness',
    score: statusScore(d.status),
    tone: statusTone(d.status),
    summary: d.detail,
    drivers: [
      {
        label: 'Posture',
        value: String(d.status || 'unknown').toUpperCase(),
        tone: statusTone(d.status),
      },
    ],
  }));

  // KPI hero band — promote the highest-signal compliance metrics to the top.
  const heroItems = [
    {
      id: 'pending-ai',
      label: 'Pending AI reviews',
      value: summary?.overview?.pendingAiReviewCount || 0,
      status: (summary?.overview?.pendingAiReviewCount || 0) > 0 ? 'warning' : 'ready',
      statusLabel: (summary?.overview?.pendingAiReviewCount || 0) > 0 ? 'Backlog' : 'Clear',
      footer: 'AI outputs awaiting human review',
    },
    {
      id: 'evidence-gaps',
      label: 'Evidence gaps',
      value: summary?.overview?.openEvidenceGapCount || 0,
      status: (summary?.overview?.openEvidenceGapCount || 0) > 0 ? 'warning' : 'ready',
      statusLabel: (summary?.overview?.openEvidenceGapCount || 0) > 0 ? 'Open' : 'Closed',
      footer: 'Compliance evidence needing sign-off',
    },
    {
      id: 'support-issues',
      label: 'Open support issues',
      value: summary?.overview?.openSupportIssueCount || 0,
      status: (summary?.overview?.openSupportIssueCount || 0) > 0 ? 'warning' : 'ready',
      statusLabel: (summary?.overview?.openSupportIssueCount || 0) > 0 ? 'Queued' : 'Clear',
      footer: 'Operational issues escalated for action',
    },
    {
      id: 'audit-events',
      label: 'Audit events',
      value: summary?.operations?.auditEventCount || 0,
      status: 'ready',
      statusLabel: 'Tracked',
      footer: 'Recent governed audit events on record',
    },
  ];

  return (
    <>
      <div className="compliance-redesign">
        <PageHeader
          eyebrow="Admin & Compliance"
          title="Compliance Readiness"
          subtitle="Track classification coverage, AI review backlog, connector/security posture, and support escalation before leadership demo or deployment."
        />

        <section className="glass-panel dark compliance-cmd-hero">
          <img
            className="compliance-cmd-hero__seal"
            src="/brand/dha-seal.svg"
            alt=""
            aria-hidden="true"
          />
          <div className="compliance-cmd-hero__body">
            <div className="eyebrow">Readiness signal</div>
            <h2 className="card-title">Security and operations command surface</h2>
            <p className="muted">
              This dashboard combines compliance posture, system readiness, operational support, and
              AI/connector monitoring into one security-review view.
            </p>
            <div className="row gap-sm compliance-cmd-hero__actions">
              <Link className="btn secondary sm" to={ENHANCEMENT_REPORT_ENTRY_PATH}>
                Draft enhancement report
              </Link>
            </div>
          </div>
          <div className={`compliance-cmd-hero__score tone-${overallTone}`}>
            <div className="compliance-cmd-hero__score-value">
              {overallScore}
              <span className="compliance-cmd-hero__score-max">/100</span>
            </div>
            <div className="compliance-cmd-hero__score-label">Domain readiness</div>
            <StatusChip status={readiness?.status || 'ready'}>
              {String(readiness?.status || 'ready').toUpperCase()}
            </StatusChip>
          </div>
        </section>

        <KpiHeroBand
          eyebrow="Readiness at a glance"
          context={`${readyDomainCount}/${controlDomains.length} domains ready`}
          items={heroItems}
          columns={4}
        />

        <section className="card">
          <div className="compliance-section-head">
            <div>
              <div className="eyebrow">Coverage</div>
              <h2 className="card-title">Coverage by domain</h2>
              <p className="muted">
                Classification, records, and readiness coverage rendered as live half-radial gauges.
              </p>
            </div>
          </div>
          <StaggerReveal className="compliance-gauge-grid">
            {coverageGauges.map((g) => (
              <GaugeCard
                key={g.id}
                id={`compliance-${g.id}`}
                value={g.value}
                min={0}
                max={100}
                unit="%"
                label={g.label}
                sublabel={g.sublabel}
                tone={g.tone}
                deltaLabel={`${g.value}%`}
                deltaTone={g.tone}
              />
            ))}
          </StaggerReveal>
        </section>

        <section className="card">
          <div className="compliance-section-head">
            <div>
              <div className="eyebrow">Control domains</div>
              <h2 className="card-title">Control domains</h2>
              <p className="muted">
                Explicit posture coverage for the security and compliance domains called out in the
                bake-off contract. Each cell carries the live readiness score and traffic-light
                tone.
              </p>
            </div>
          </div>
          <div className="compliance-heatmap-shell">
            <div className="compliance-heatmap-frame">
              <MissionImpactHeatmap
                rows={heatmapRows}
                columns={heatmapColumns}
                cells={heatmapCells}
                cellSize={52}
                ariaLabel="Control-domain readiness heatmap — domains by readiness score"
              />
            </div>
            <div className="compliance-heatmap-legend" aria-hidden="true">
              <span className="legend-swatch">
                <span className="legend-dot tone-sage" />
                Ready
              </span>
              <span className="legend-swatch">
                <span className="legend-dot tone-amber" />
                Warning
              </span>
              <span className="legend-swatch">
                <span className="legend-dot tone-brick" />
                Degraded
              </span>
            </div>
          </div>
        </section>
      </div>

      <section className="bento admin-bento">
        <div className="span-4 card">
          <div className="eyebrow">System posture</div>
          <h2 className="card-title">System health dashboard</h2>
          <ul className="compliance-list">
            {readinessItems.map((item) => (
              <ReadinessItem key={item.label} item={item} />
            ))}
          </ul>
        </div>

        <div className="span-4 card">
          <div className="eyebrow">Data domains</div>
          <h2 className="card-title">Data operations dashboard</h2>
          <ul className="compliance-list">
            <li>
              <strong>Failed syncs</strong>
              <small>{summary?.dataOperations?.failedSyncCount || 0} require steward review</small>
            </li>
            <li>
              <strong>Failed imports</strong>
              <small>{summary?.dataOperations?.failedImportCount || 0} staging jobs blocked</small>
            </li>
            <li>
              <strong>Unresolved reconciliation</strong>
              <small>
                {summary?.dataOperations?.unresolvedReconciliationCount || 0} cases remain open
              </small>
            </li>
            <li>
              <strong>Stale sources</strong>
              <small>{summary?.dataOperations?.staleSourceCount || 0} connectors degraded</small>
            </li>
          </ul>
          <div className="row gap-sm">
            <Link className="btn ghost sm" to="/admin/data">
              Open data operations
            </Link>
          </div>
        </div>

        <div className="span-4 card">
          <div className="eyebrow">AI telemetry</div>
          <h2 className="card-title">AI operations dashboard</h2>
          <ul className="compliance-list">
            <li>
              <strong>Recent AI runs</strong>
              <small>{summary?.aiOperations?.recentRunCount || 0} tracked runs</small>
            </li>
            <li>
              <strong>Failed AI runs</strong>
              <small>{summary?.aiOperations?.failedRunCount || 0} require review</small>
            </li>
            <li>
              <strong>Provider modes</strong>
              <small>
                {(summary?.aiOperations?.providerModes || []).join(', ') || 'No provider metadata'}
              </small>
            </li>
            <li>
              <strong>Citation failures</strong>
              <small>
                {summary?.aiOperations?.citationFailureCount || 0} draft outputs blocked
              </small>
            </li>
          </ul>
          <div className="row gap-sm">
            <Link className="btn ghost sm" to="/ai/orchestration">
              Open AI operations
            </Link>
          </div>
        </div>

        <div className="span-4 card">
          <div className="eyebrow">Evidence gaps</div>
          <h2 className="card-title">Open compliance evidence</h2>
          {(summary?.evidenceGaps || []).length === 0 ? (
            <EmptyState
              title="No evidence gaps"
              description="Current controls all have reviewed evidence."
            />
          ) : (
            <ul className="compliance-list">
              {summary.evidenceGaps.map((item) => (
                <li key={item.id}>
                  <strong>{item.title || item.id}</strong>
                  <small>{item.status || 'needs review'}</small>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="span-4 card">
          <div className="eyebrow">Security findings</div>
          <h2 className="card-title">Open findings</h2>
          {(summary?.securityFindings || []).length === 0 ? (
            <EmptyState
              title="No open findings"
              description="No unresolved security findings are currently tracked."
            />
          ) : (
            <ul className="compliance-list">
              {summary.securityFindings.map((item) => (
                <li key={item.id}>
                  <strong>{item.title || item.id}</strong>
                  <small>{item.severity || item.status || 'open'}</small>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="span-4 card">
          <div className="eyebrow">Privacy controls</div>
          <h2 className="card-title">Sensitive field controls</h2>
          {(summary?.sensitiveFieldPolicies || []).length === 0 ? (
            <EmptyState
              title="No sensitive fields configured"
              description="No active field-level disclosure controls are currently registered."
            />
          ) : (
            <ul className="compliance-list">
              {summary.sensitiveFieldPolicies.map((policy) => (
                <li key={policy.id}>
                  <div className="compliance-policy-copy">
                    <strong>{policy.label}</strong>
                    <small>
                      {policy.linkedForms?.length
                        ? policy.linkedForms.join(', ')
                        : `${policy.entityType || 'record'} field`}
                    </small>
                  </div>
                  <PolicyMeta policy={policy} />
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="span-4 card">
          <div className="eyebrow">Access and scan posture</div>
          <h2 className="card-title">Review and scan backlog</h2>
          <ul className="compliance-list">
            <li>
              <strong>Open access reviews</strong>
              <small>{summary?.overview?.openAccessReviewCount || 0} require action</small>
            </li>
            <li>
              <strong>Failed scans</strong>
              <small>{summary?.operations?.failedScanCount || 0} findings need closure</small>
            </li>
            <li>
              <strong>Masked disclosure views</strong>
              <small>
                {summary?.overview?.maskedSensitiveFieldCount || 0} controlled fields enforced
              </small>
            </li>
          </ul>
        </div>

        <div className="span-4 card">
          <div className="eyebrow">Access recertification</div>
          <h2 className="card-title">Open access reviews</h2>
          {(summary?.accessReviews || []).length === 0 ? (
            <EmptyState
              title="No open access reviews"
              description="No access-review items are currently pending."
            />
          ) : (
            <ul className="compliance-list">
              {summary.accessReviews.map((item) => (
                <li key={item.id}>
                  <strong>{item.title || item.id}</strong>
                  <small>{item.status || 'open'}</small>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="span-4 card">
          <div className="eyebrow">STIG and scan gaps</div>
          <h2 className="card-title">Open scan findings</h2>
          {(summary?.scanResults || []).length === 0 ? (
            <EmptyState
              title="No failed scans"
              description="Current scan posture is clear of open findings."
            />
          ) : (
            <ul className="compliance-list">
              {summary.scanResults.map((item) => (
                <li key={item.id}>
                  <strong>{item.title || item.id}</strong>
                  <small>{item.severity || item.status || 'open'}</small>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="span-4 card">
          <div className="eyebrow">Incident queue</div>
          <h2 className="card-title">Recent incidents</h2>
          {(summary?.recentIncidents || []).length === 0 ? (
            <EmptyState
              title="No recent incidents"
              description="No critical operational incidents are currently queued."
            />
          ) : (
            <ul className="compliance-list">
              {summary.recentIncidents.map((item, index) => (
                <li key={`${item.kind}-${item.title}-${index}`}>
                  <strong>{item.title}</strong>
                  <small>{item.kind.replace(/_/g, ' ')}</small>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="span-4 card">
          <div className="eyebrow">Audit activity</div>
          <h2 className="card-title">Recent audit activity</h2>
          <HistoryList
            items={summary?.auditActivity || []}
            emptyTitle="No audit activity"
            emptyDescription="Recent governed audit events will appear here."
            renderLabel={(item) => item.id}
            renderMeta={(item) =>
              [item.summary, item.actorUserId].filter(Boolean).join(' · ') || 'Audit event recorded'
            }
          />
          <div className="row gap-sm">
            <Link className="btn ghost sm" to="/admin/audit">
              Open audit log
            </Link>
          </div>
        </div>

        <div className="span-4 card">
          <div className="eyebrow">Support posture</div>
          <h2 className="card-title">Support metrics</h2>
          <ul className="compliance-list">
            <li>
              <strong>Critical / high</strong>
              <small>
                {summary?.supportMetrics?.criticalCount || 0} critical ·{' '}
                {summary?.supportMetrics?.highCount || 0} high
              </small>
            </li>
            <li>
              <strong>Triaged queue</strong>
              <small>{summary?.supportMetrics?.triagedCount || 0} issues already triaged</small>
            </li>
            <li>
              <strong>{`${summary?.supportMetrics?.averageAgeHours || 0}h average age`}</strong>
              <small>Average time open across active support issues</small>
            </li>
          </ul>
          <div className="row gap-sm">
            <Link className="btn ghost sm" to="/feedback/queue">
              Open feedback queue
            </Link>
          </div>
        </div>

        <div className="span-12 card">
          <div className="eyebrow">Operational telemetry</div>
          <h2 className="card-title">Run history drilldowns</h2>
          <div className="compliance-history-grid">
            <section className="compliance-history-section">
              <strong>Recent sync runs</strong>
              <HistoryList
                items={summary?.runHistory?.syncRuns || []}
                emptyTitle="No failed syncs"
                emptyDescription="Recent connector sync failures will appear here."
                renderLabel={(item) => item.id}
                renderMeta={(item) =>
                  `${String(item.status || 'unknown').replace(/_/g, ' ')}${item.connectorId ? ` · ${item.connectorId}` : ''}`
                }
              />
            </section>

            <section className="compliance-history-section">
              <strong>Recent ingestion jobs</strong>
              <HistoryList
                items={summary?.runHistory?.ingestionJobs || []}
                emptyTitle="No failed ingestion jobs"
                emptyDescription="Validation and commit failures will appear here."
                renderLabel={(item) => item.id}
                renderMeta={(item) =>
                  `${String(item.status || 'unknown').replace(/_/g, ' ')}${item.sourceSystem ? ` · ${item.sourceSystem}` : ''}`
                }
              />
            </section>

            <section className="compliance-history-section">
              <strong>Recent AI run failures</strong>
              <HistoryList
                items={summary?.runHistory?.aiRuns || []}
                emptyTitle="No failed AI runs"
                emptyDescription="Provider and citation failures will appear here."
                renderLabel={(item) => item.id}
                renderMeta={(item) =>
                  `${String(item.status || 'unknown').replace(/_/g, ' ')}${item.providerMode ? ` · ${item.providerMode}` : ''}`
                }
              />
            </section>

            <section className="compliance-history-section">
              <strong>Recent export failures</strong>
              <HistoryList
                items={summary?.runHistory?.exports || []}
                emptyTitle="No failed exports"
                emptyDescription="Blocked or failed leadership-product exports will appear here."
                renderLabel={(item) => item.title || item.id}
                renderMeta={(item) => String(item.status || 'unknown').replace(/_/g, ' ')}
              />
            </section>
          </div>
        </div>

        <div className="span-12 card">
          <div className="eyebrow">Support dashboard</div>
          <h2 className="card-title">Operational support issues</h2>
          {supportIssues.length === 0 ? (
            <EmptyState
              title="No support issues"
              description="No open support concerns are queued right now."
            />
          ) : (
            <ul className="compliance-support-list">
              {supportIssues.map((issue) => (
                <li key={issue.id}>
                  <div>
                    <strong>{issue.title}</strong>
                    <div className="muted small">
                      {issue.id} · {String(issue.status || 'open').replace(/_/g, ' ')} ·{' '}
                      {issue.severity || 'medium'}
                    </div>
                  </div>
                  {issue.convertedActionId ? (
                    <Link
                      className="chip sage sm"
                      to="/actions"
                      data-testid={`issue-converted-${issue.id}`}
                    >
                      Converted → {issue.convertedActionId}
                    </Link>
                  ) : (
                    <button
                      type="button"
                      className="btn primary sm"
                      disabled={convertMutation.running}
                      onClick={() => convertToAction(issue)}
                    >
                      {convertingIssueId === issue.id ? 'Converting…' : 'Convert to action'}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </>
  );
}
